import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Drawer, Dropdown, Tooltip } from "antd";
import {
  DashboardOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  BgColorsOutlined,
  BankOutlined,
  QuestionCircleOutlined,
  PercentageOutlined,
  LogoutOutlined,
  MenuOutlined,
  AppstoreOutlined,
  TagsOutlined,
  GiftOutlined,
  CloseOutlined,
  UserOutlined,
  ExportOutlined,
  TeamOutlined,
  MailOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { useClient } from "../../context/ClientContext";
import { resolveImgUrl } from "../../templates/4/utils";
import { hexAlpha } from "../../utils/colorUtils";

// roles: además del dueño (1), quién más lo ve: vendedor (3), tesorero (4).
// Sin roles es solo del dueño.
const menuItems = [
  { key: "admin",            label: "Dashboard",       icon: DashboardOutlined },
  { key: "admin/venta",      label: "Nuevo pedido",    icon: ShoppingCartOutlined, roles: [3] },
  { key: "admin/pedidos",    label: "Pedidos",         icon: ShoppingOutlined,     roles: [3] },
  { key: "admin/cobros",     label: "Cobros",          icon: DollarOutlined,       roles: [4] },
  { key: "admin/clientes",   label: "Clientes",        icon: UserOutlined },
  { key: "admin/productos",  label: "Productos",       icon: AppstoreOutlined },
  { key: "admin/categorias", label: "Categorías",      icon: TagsOutlined },
  { key: "admin/promociones",label: "Promociones",   icon: GiftOutlined },
  { key: "admin/margenes",   label: "Márgenes",        icon: PercentageOutlined, capacidad: "precios_automaticos" },
  { key: "admin/landing",    label: "Config. Landing", icon: BgColorsOutlined },
  { key: "admin/faqs",       label: "Preguntas frecuentes", icon: QuestionCircleOutlined },
  { key: "admin/cuentas",        label: "Cuentas transf.", icon: BankOutlined,      roles: [4] },
  { key: "admin/transferencias", label: "Transferencias",  icon: BarChartOutlined,  roles: [4] },
  { key: "admin/colaboradores",  label: "Colaboradores",   icon: TeamOutlined },
  { key: "admin/emails",         label: "Mails enviados",  icon: MailOutlined },
];


function SidebarItem({ item, isSelected, primary, onClick }) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;
  const bg = isSelected ? hexAlpha(primary, 0.10) : hovered ? hexAlpha(primary, 0.05) : "transparent";
  const color = isSelected ? primary : hovered ? primary : "#4b5563";
  const fontWeight = isSelected ? 600 : 400;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 16px",
        borderRadius: 8,
        margin: "2px 10px",
        cursor: "pointer",
        background: bg,
        color,
        fontWeight,
        fontSize: 14,
        transition: "background 0.15s, color 0.15s",
        position: "relative",
      }}
    >
      {isSelected && (
        <div style={{
          position: "absolute", left: 0, top: "20%", bottom: "20%",
          width: 3, borderRadius: "0 3px 3px 0",
          background: primary,
        }} />
      )}
      <Icon style={{ fontSize: 16, flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{item.label}</span>
    </div>
  );
}

const getUserInitials = (user) => {
  if (!user) return "U";
  if (user.nombre || user.apellido)
    return ((user.nombre?.[0] || "") + (user.apellido?.[0] || "")).toUpperCase();
  return (user.usuario?.[0] || user.email?.[0] || "U").toUpperCase();
};

export const AdminLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { tienda, user, logout } = useAuth();
  // El vendedor entra al mismo admin pero solo ve lo que puede usar.
  const { clientPath, theme, config, tieneCapacidad } = useClient();
  // Lo que depende de una capacidad aparece solo si la tienda la tiene prendida.
  const visibles = (user?.id_rol === 1 ? menuItems : menuItems.filter(m => m.roles?.includes(user?.id_rol)))
    .filter(m => !m.capacidad || tieneCapacidad(m.capacidad));
  const logo = config?.logo;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const isLandingEditor = location.pathname.includes("/admin/landing");
  // Gana el ítem más específico que contenga la ruta: así el detalle de un
  // pedido o de un cliente deja marcada su sección y no el Dashboard.
  const selectedKey = menuItems
    .filter(m => location.pathname.endsWith(`/${m.key}`) || location.pathname.includes(`/${m.key}/`))
    .sort((a, b) => b.key.length - a.key.length)[0]?.key || "admin";
  const currentSection = menuItems.find(m => m.key === selectedKey)?.label || "Admin";

  const primary = theme?.primaryColor || "#1677ff";
  const displayName = tienda?.display_name || tienda?.nombre || "Admin";
  const initials = displayName.slice(0, 2).toUpperCase();
  const logoSrc = logo?.dark || logo?.desktop || logo?.mobile || null;

  const handleSelect = (key) => {
    navigate(clientPath(`/${key}`));
    setMobileOpen(false);
  };

  const sidebarContent = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff" }}>
      {/* Brand */}
      <div style={{
        padding: "20px 16px 16px",
        borderBottom: `1px solid ${hexAlpha(primary, 0.10)}`,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        cursor: "pointer",
        background: hexAlpha(primary, 0.04),
      }}
        onClick={() => navigate(clientPath("/admin"))}
      >
        {logoSrc && !logoError ? (
          <div style={{
            width: 52, height: 52, borderRadius: "50%", overflow: "hidden",
            background: hexAlpha(primary, 0.10),
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <img
              src={resolveImgUrl(logoSrc)}
              alt={displayName}
              style={{ width: "80%", height: "80%", objectFit: "contain" }}
              onError={() => setLogoError(true)}
            />
          </div>
        ) : (
          <div style={{
            width: 52, height: 52, borderRadius: "50%", flexShrink: 0,
            background: hexAlpha(primary, 0.14),
            color: primary, fontWeight: 800, fontSize: 18,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {initials}
          </div>
        )}
        <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "center" }}>Panel de administración</div>
      </div>

      {/* Menú */}
      <nav style={{ flex: 1, paddingTop: 10, overflowY: "auto" }}>
        {visibles.map(item => (
          <SidebarItem
            key={item.key}
            item={item}
            isSelected={selectedKey === item.key}
            primary={primary}
            onClick={() => handleSelect(item.key)}
          />
        ))}
      </nav>

    </div>
  );

  const geistFont = "'Geist', system-ui, -apple-system, sans-serif";

  // Modo editor de landing: layout minimalista
  if (isLandingEditor) {
    return (
      <div className="admin-layout-geist" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: geistFont }}>
        <div style={{
          background: "#fff", borderBottom: "1px solid #f3f4f6",
          padding: "0 20px", height: 56,
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <button
            onClick={() => navigate(clientPath("/admin"))}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "none", border: "none", cursor: "pointer",
              fontWeight: 600, color: "#6b7280", fontSize: 13, padding: "6px 10px",
              borderRadius: 6,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            <CloseOutlined /> Salir del editor
          </button>
          <div style={{ width: 1, height: 20, background: "#e5e7eb" }} />
          <span style={{ fontSize: 13, color: "#9ca3af" }}>Editor de diseño — {displayName}</span>
        </div>
        <div style={{ flex: 1, background: "#f5f6fa", padding: 24 }}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout-geist" style={{ display: "flex", minHeight: "100vh", background: "#f5f6fa", fontFamily: geistFont }}>
      <style>{`
        .admin-layout-geist,
        .admin-layout-geist * {
          font-family: 'Geist', system-ui, -apple-system, sans-serif !important;
        }
        .admin-layout-geist .ant-typography,
        .admin-layout-geist .ant-btn,
        .admin-layout-geist .ant-input,
        .admin-layout-geist .ant-select,
        .admin-layout-geist .ant-input-number,
        .admin-layout-geist .ant-switch,
        .admin-layout-geist .ant-checkbox,
        .admin-layout-geist .ant-modal,
        .admin-layout-geist .ant-drawer,
        .admin-layout-geist .ant-message,
        .admin-layout-geist .ant-tag,
        .admin-layout-geist .ant-alert,
        .admin-layout-geist .ant-pagination,
        .admin-layout-geist .ant-spin,
        .admin-layout-geist .ant-divider,
        .admin-layout-geist h1, .admin-layout-geist h2, .admin-layout-geist h3,
        .admin-layout-geist h4, .admin-layout-geist h5, .admin-layout-geist h6,
        .admin-layout-geist p, .admin-layout-geist span, .admin-layout-geist div,
        .admin-layout-geist label, .admin-layout-geist input, .admin-layout-geist select,
        .admin-layout-geist textarea, .admin-layout-geist button {
          font-family: 'Geist', system-ui, -apple-system, sans-serif !important;
        }
      `}</style>
      {/* Sidebar desktop */}
      {!isMobile && (
        <aside style={{
          width: 240, flexShrink: 0,
          borderRight: "1px solid #f3f4f6",
          boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
          position: "sticky", top: 0, height: "100vh", overflowY: "auto",
        }}>
          {sidebarContent}
        </aside>
      )}

      {/* Mobile drawer */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        placement="left"
        width={240}
        styles={{ body: { padding: 0 }, header: { display: "none" } }}
        title={null}
        closable={false}
      >
        {sidebarContent}
      </Drawer>

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Top header */}
        <header style={{
          background: "#fff",
          borderBottom: "1px solid #f3f4f6",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          height: 56,
          padding: "0 20px",
          display: "flex", alignItems: "center", gap: 12,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          {isMobile && (
            <button
              onClick={() => setMobileOpen(true)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, borderRadius: 8,
                color: "#374151",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
          )}

          {/* Breadcrumb / sección actual */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
            {isMobile && logoSrc && (
              <img
                src={resolveImgUrl(logoSrc)}
                alt={displayName}
                style={{ height: 28, maxWidth: 100, objectFit: "contain" }}
              />
            )}
            <span style={{
              fontSize: 15, fontWeight: 600, color: "#111827",
            }}>
              {currentSection}
            </span>
          </div>

          {/* Acciones header */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Ver tienda */}
            <Tooltip title={`Ir a ${displayName}`}>
              <button
                onClick={() => window.open(clientPath("/"), "_blank")}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: "none", border: "none",
                  borderRadius: 8, padding: "5px 12px",
                  cursor: "pointer", color: primary, fontSize: 13, fontWeight: 500,
                  transition: "border-color 0.15s, background 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.color = primary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#374151"; }}
              >
                <ExportOutlined style={{ fontSize: 13 }} />
                {!isMobile && "Ver tienda"}
              </button>
            </Tooltip>

            {/* Avatar usuario con dropdown */}
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: "info",
                    label: (
                      <div style={{ pointerEvents: "none", padding: "2px 0" }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>
                          {user?.nombre} {user?.apellido}
                        </div>
                        {user?.usuario && (
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>{user.usuario}</div>
                        )}
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: "divider" },
                  {
                    key: "logout",
                    label: "Cerrar sesión",
                    icon: <LogoutOutlined />,
                    danger: true,
                    onClick: logout,
                  },
                ],
              }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                background: hexAlpha(primary, 0.14),
                color: primary, fontWeight: 700, fontSize: 13,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", flexShrink: 0,
                border: `1.5px solid ${hexAlpha(primary, 0.25)}`,
                userSelect: "none",
              }}>
                {user ? getUserInitials(user) : <UserOutlined style={{ fontSize: 14 }} />}
              </div>
            </Dropdown>
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: isMobile ? 16 : 24 }}>
          {children}
        </main>
      </div>

    </div>
  );
};
